import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import EmptyList from 'components/EmptyList/EmptyList';
import IconButton from 'components/IconButton/IconButton';
import { FIELD_KEYS } from 'constants/materialAssessment';

import TableHeader from './components/TableHeader/TableHeader';

import type ISectorRiskAnalyzeItem from 'store/slices/materialAssessment/interfaces/ISectorRiskAnalyzeItem';
import type ISectorSpecificItem from 'store/slices/standards/interfaces/ISectorSpecificItem';
import type ITableAnalyze from './interfaces/ITableAnalyze';

import styles from './scss/TableAnalyze.module.scss';

function TableAnalyze({
  items, headCells, headCellsMiniTable, onLoadMore,
  hasMore, onDeleteItem, naceCodesData, expandedRow, setExpandedRow,
  itemsAnalyzed, onSortChange, orderBy, locationsCountries,
}: ITableAnalyze) {
  const { t } = useTranslation();

  const toggleRow = (id: number) => {
    setExpandedRow(id);
  };

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  const renderMainTableCell = (item: ISectorSpecificItem, cellId: string) => {
    switch (cellId) {
      case FIELD_KEYS.COUNTRY:
        return (
          <div className={styles.countryCell}>
            <DangerIcon className={styles.dangerIcon} />
            <span>{locationCountry(item.country)}</span>
          </div>
        );
      case FIELD_KEYS.INDUSTRY_CODE:
        return naceCodesData.find((nace) => item.naceSection?.includes(nace.value))?.divisions
          ?.filter((division) => item.naceCodes?.includes(division.value))
          ?.map((division, divisionIndex, filteredDivisions) => (
            <span key={division.label}>
              {division.label}
              {divisionIndex < filteredDivisions.length - 1 && ', '}
            </span>
          ));
      case FIELD_KEYS.SECTOR_CODE:
        return naceCodesData.find((nace) => item.naceSection?.includes(nace.value))?.label;
      case FIELD_KEYS.ACTIONS:
        return (
          <div className={styles.actions}>
            {item.custom ? (
              <IconButton
                icon={<TrashIcon />}
                onClick={() => onDeleteItem?.(item.id || -1)}
              />
            ) : null}
          </div>
        );
      default:
        return '';
    }
  };

  const renderMiniTableCell = (analyzeItem: ISectorRiskAnalyzeItem, cellId: string) => {
    switch (cellId) {
      case FIELD_KEYS.ROLE:
        return analyzeItem.type;
      case FIELD_KEYS.FULL_NAME:
        return analyzeItem.name;
      case FIELD_KEYS.INDUSTRY:
        return analyzeItem.naceSection;
      case FIELD_KEYS.STATE:
        return analyzeItem.addressState;
      default:
        return '';
    }
  };

  const handleCheckHasAffected = (item: ISectorSpecificItem) => {
    let checkItem = false;
    const { hasAffected } = item;
    if (hasAffected) {
      Object.keys(hasAffected).forEach((key) => {
        if (hasAffected[Number(key)]) {
          checkItem = true;
        }
      });
    }
    return checkItem;
  };

  return (
    <div className={styles.root}>
      <TableHeader headCells={headCells} onSortChange={onSortChange} orderBy={orderBy} />
      <div className={styles.tableBody}>
        {items.map((item, index) => {
          const isExpanded = expandedRow === (item.id || -1);
          return (
            <div key={[item.id, index].join('-')} className={styles.tableRowGroup}>
              <div className={styles.tableRow}>
                <div className={styles.expandColumn}>
                  {handleCheckHasAffected(item) && (
                    <button
                      type="button"
                      className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
                      onClick={() => toggleRow(item.id || -1)}
                      aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
                    >
                      <ChevronIcon />
                    </button>
                  )}
                </div>
                {headCells.map((cell) => (
                  <div key={cell.id} className={styles.tableCell}>
                    {renderMainTableCell(item, cell.id)}
                  </div>
                ))}
              </div>

              {isExpanded && itemsAnalyzed ? (
                <div className={styles.miniTableContainer}>
                  <div className={styles.miniTableHeader}>
                    {headCellsMiniTable.map((cell) => (
                      <div key={cell.id} className={styles.miniHeaderCell}>
                        {t(cell.label)}
                      </div>
                    ))}
                  </div>

                  <div className={styles.miniTableBody}>
                    {itemsAnalyzed && itemsAnalyzed.length > 0 ? itemsAnalyzed.map((analyzeItem) => (
                      <div key={analyzeItem.id} className={styles.miniTableRow}>
                        {headCellsMiniTable.map((cell) => (
                          <div key={cell.id} className={styles.miniTableCell}>
                            {renderMiniTableCell(analyzeItem, cell.id)}
                          </div>
                        ))}
                      </div>
                    )) : <EmptyList text={t('common.noResultsItems')} />}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {hasMore ? (
        <div className={styles.loadMoreContainer}>
          <button
            type="button"
            className={styles.loadMoreButton}
            onClick={onLoadMore}
          >
            {t('common.loadMore')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default TableAnalyze;
